import type {
  OrgChatMessageDeliveryResult,
  OrgChatSendOptions,
} from '@/contexts/org-chat-interface-context';

/** Null is the server's backwards-compatible receipt for a durable deferred send. */
export function isSendAccepted(result: unknown): boolean {
  if (result === null) return true;
  if (typeof result !== 'object' || !result || 'status' in result) return false;
  return ['orgThreadId', 'messageId', 'workflowId'].every(
    (key) =>
      key in result &&
      typeof (result as Record<string, unknown>)[key] === 'string' &&
      ((result as Record<string, unknown>)[key] as string).length > 0
  );
}

export function assertSendAccepted(result: unknown): void {
  if (!isSendAccepted(result)) throw new Error('Message send was not accepted');
}

type ThreadTarget<T> = { routeRef: string; threadId: T };

/** Every requested destination gets a receipt, including lookup and creation failures. */
export async function dispatchThreadTargets<T>(args: {
  existingRouteRefs: string[];
  newRouteRefs: string[];
  options?: OrgChatSendOptions;
  resolve: (routeRefs: string[]) => Promise<ThreadTarget<T>[]>;
  create?: (routeRef: string) => Promise<T>;
  createAndSend?: (routeRef: string) => Promise<unknown>;
  send: (threadId: T, routeRef: string) => Promise<unknown>;
}): Promise<OrgChatMessageDeliveryResult> {
  const resolved = new Map<string, T>();
  let lookupError: unknown;
  try {
    for (const target of await args.resolve(args.existingRouteRefs))
      resolved.set(target.routeRef, target.threadId);
  } catch (error) {
    lookupError = error;
  }
  const destinations = [
    ...args.existingRouteRefs.map((routeRef) => ({ routeRef, create: false })),
    ...args.newRouteRefs.map((routeRef) => ({ routeRef, create: true })),
  ];
  const outcomes = await Promise.all(
    destinations.map(async (target) => {
      let threadId = resolved.get(target.routeRef);
      let created = false;
      try {
        if (target.create && args.createAndSend) {
          assertSendAccepted(await args.createAndSend(target.routeRef));
          return {
            routeRef: target.routeRef,
            accepted: true,
            needsCreation: false,
          };
        }
        if (target.create) {
          if (!args.create) throw new Error('Thread creation is unavailable');
          threadId = await args.create(target.routeRef);
          created = true;
        }
        if (threadId === undefined)
          throw lookupError ?? new Error('Thread is unavailable');
        assertSendAccepted(await args.send(threadId, target.routeRef));
        return {
          routeRef: target.routeRef,
          accepted: true,
          needsCreation: false,
        };
      } catch (error) {
        return {
          routeRef: target.routeRef,
          accepted: false,
          error: error instanceof Error ? error.message : 'Message send failed',
          needsCreation: target.create && !created,
        };
      }
    })
  );
  const failures = outcomes.filter((target) => !target.accepted);
  return {
    status: 'started',
    accepted: outcomes.length > 0 && failures.length === 0,
    targets: outcomes.map(
      ({ needsCreation: _needsCreation, ...target }) => target
    ),
    ...(failures.length
      ? {
          retryOptions: {
            ...args.options,
            targetThreadRouteRef: undefined,
            targetThreadRouteRefs: failures
              .filter((target) => !target.needsCreation)
              .map((target) => target.routeRef),
            newThreadRouteRefs: failures
              .filter((target) => target.needsCreation)
              .map((target) => target.routeRef),
            newThreadCount: 0,
          },
        }
      : {}),
  };
}

/** Voice sessions use their own transcript transport; typed sends must do work. */
export function assertTextSendOptions(
  options?: Pick<OrgChatSendOptions, 'voiceMode'>
): void {
  if (options?.voiceMode)
    throw new Error('UNSUPPORTED_VOICE_MESSAGE_TRANSPORT');
}
